import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronUpIcon, ChevronDownIcon, MinusSmIcon, PlusSmIcon } from "@heroicons/react/solid";
import { Disclosure } from "@headlessui/react";
import ReactMarkdown from "react-markdown";

export default function QA({ data, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                setContentHeight(entry.contentRect.height);
            }
        });
        resizeObserver.observe(contentRef.current);
        return () => resizeObserver.disconnect();
    }
  }, [isOpen]);

  return (
    <div className="w-full mb-8 border-t-2 border-b-2 border-neutral-300">
      <button
        className="flex w-full items-center justify-center py-2 text-2xl font-light lg:py-4 lg:text-[40px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="mr-4">{label}</span>
        {isOpen ? (
          <ChevronUpIcon className="w-6 lg:w-12" />
        ) : (
          <ChevronDownIcon className="w-6 lg:w-12" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="w-full overflow-hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: contentHeight, opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 140,
              damping: 15,
              duration: 0.2,
            }}
          >
            <div ref={contentRef} className="pb-4">
              <div className="mt-10 lg:col-span-7 lg:mt-0">
                <dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
                  {data.length > 0
                    ? data.map((qa) => (
                        <Disclosure as="div" key={qa.id} className="pt-6">
                          {({ open }) => (
                            <>
                              <dt>
                                <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
                                  <span className="text-base font-semibold leading-7">
                                    {qa.question}
                                  </span>
                                  <span className="ml-6 flex h-7 items-center">
                                    {open ? (
                                      <MinusSmIcon
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <PlusSmIcon
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </Disclosure.Button>
                              </dt>
                              <Disclosure.Panel as="dd" className="mt-2 pr-12">
                                <ReactMarkdown
                                  as="div"
                                  className="font-body mt-10 prose-h2:uppercase hover:prose-a:underline prose-li:my-2 lg:max-w-[100ch]"
                                >
                                  {qa.answer}
                                </ReactMarkdown>
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      ))
                    : null}
                </dl>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
